import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, toDisplayString as _toDisplayString, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, withCtx as _withCtx, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "row mmb-5" }
const _hoisted_2 = { class: "col span-6" }
const _hoisted_3 = { class: "col span-6" }
const _hoisted_4 = { class: "row mmb-5" }
const _hoisted_5 = { class: "col span-6" }
const _hoisted_6 = { class: "col span-6" }

import { computed } from 'vue';
import { useStore } from 'vuex';
import ArrayListGrouped from '@shell/components/form/ArrayListGrouped';
import Banner from '@components/Banner/Banner.vue';
import { LabeledInput } from '@components/Form/LabeledInput';
import { Checkbox } from '@components/Form/Checkbox';
import { _EDIT } from '@shell/config/query-params';
import { RadioGroup } from '@components/Form/Radio';
import { useI18n } from '@shell/composables/useI18n';
import { ACME_SERVERS, ACME_SERVER_CHOICE } from '../../form-options';
import type { AcmeIssuerConfig } from '../../schema';
import AcmeSolver from './AcmeSolver.vue';

type ServerChoice = typeof ACME_SERVER_CHOICE[keyof typeof ACME_SERVER_CHOICE];

interface Props {
  /** The live `spec.acme` object. */
  value: AcmeIssuerConfig;
  mode?: string;
  /** Secret refs on a ClusterIssuer resolve in cert-manager's own namespace, not the user's. */
  clusterScoped?: boolean;
  /**
   * Validators keyed by field, owned by the parent form. Bound to the inputs rather than
   * checked at the form level so a `required` message waits for the field to be blurred - the
   * ACME defaults leave both of these empty, and a banner on arrival reads as an error the
   * user caused.
   */
  rules?: Record<string, any>;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'AcmeConfig',
  props: {
    value: {},
    mode: { default: _EDIT },
    clusterScoped: { type: Boolean, default: false },
    rules: { default: () => ({}) }
  },
  setup(__props: any, { expose: __expose }) {

const props = __props;

const store = useStore();
const { t } = useI18n(store);

props.value.privateKeySecretRef = props.value.privateKeySecretRef || {};
props.value.solvers = props.value.solvers || [];

const serverChoiceOptions = computed(() => Object.values(ACME_SERVER_CHOICE).map((value) => ({
  value,
  label: t(`certManager.issuer.acme.serverChoice.${ value }`),
})));

/** Derived from the URL so an issuer authored in YAML still lands on the right option. */
const serverChoice = computed<ServerChoice>({
  get() {
    if (props.value.server === ACME_SERVERS.PRODUCTION) {
      return ACME_SERVER_CHOICE.PRODUCTION;
    }

    return props.value.server === ACME_SERVERS.STAGING ? ACME_SERVER_CHOICE.STAGING : ACME_SERVER_CHOICE.CUSTOM;
  },
  set(choice) {
    if (choice === ACME_SERVER_CHOICE.PRODUCTION) {
      props.value.server = ACME_SERVERS.PRODUCTION;
    } else if (choice === ACME_SERVER_CHOICE.STAGING) {
      props.value.server = ACME_SERVERS.STAGING;
    } else {
      props.value.server = '';
    }
  },
});

const isCustomServer = computed(() => serverChoice.value === ACME_SERVER_CHOICE.CUSTOM);

const privateKeySecretName = computed<string | undefined>({
  get() {
    return props.value.privateKeySecretRef?.name;
  },
  set(neu) {
    if (!props.value.privateKeySecretRef) {
      props.value.privateKeySecretRef = {};
    }
    props.value.privateKeySecretRef.name = neu;
  },
});

const solvers = computed<any[]>({
  get() {
    return props.value.solvers || [];
  },
  set(neu) {
    props.value.solvers = neu;
  },
});

const secretNamespaceTooltip = computed(() => (props.clusterScoped ? t('certManager.issuer.acme.secretNamespaceCluster') : t('certManager.issuer.acme.secretNamespace')));

__expose({
  serverChoice, serverChoiceOptions, isCustomServer
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", null, [
    _createVNode(_unref(RadioGroup), {
      value: serverChoice.value,
      "onUpdate:value": _cache[0] || (_cache[0] = ($event: any) => ((serverChoice).value = $event)),
      name: "acmeServerChoice",
      label: _unref(t)('certManager.issuer.acme.server'),
      options: serverChoiceOptions.value,
      mode: __props.mode,
      class: "mmb-5"
    }, null, 8, ["value", "label", "options", "mode"]),
    _cache[9] || (_cache[9] = _createTextVNode()),
    _createElementVNode("div", _hoisted_1, [
      _createElementVNode("div", _hoisted_2, [
        _createVNode(_unref(LabeledInput), {
          value: __props.value.server,
          "onUpdate:value": _cache[1] || (_cache[1] = ($event: any) => ((__props.value.server) = $event)),
          label: _unref(t)('certManager.issuer.acme.serverUrl'),
          tooltip: _unref(t)('certManager.issuer.acme.serverTooltip'),
          mode: __props.mode,
          disabled: !isCustomServer.value,
          rules: __props.rules.server || [],
          required: "",
          "data-testid": "cert-manager-issuer-acme-server"
        }, null, 8, ["value", "label", "tooltip", "mode", "disabled", "rules"])
      ]),
      _cache[7] || (_cache[7] = _createTextVNode()),
      _createElementVNode("div", _hoisted_3, [
        _createVNode(_unref(LabeledInput), {
          value: __props.value.email,
          "onUpdate:value": _cache[2] || (_cache[2] = ($event: any) => ((__props.value.email) = $event)),
          label: _unref(t)('certManager.issuer.acme.email'),
          tooltip: _unref(t)('certManager.issuer.acme.emailTooltip'),
          mode: __props.mode
        }, null, 8, ["value", "label", "tooltip", "mode"])
      ])
    ]),
    _cache[10] || (_cache[10] = _createTextVNode()),
    _createElementVNode("div", _hoisted_4, [
      _createElementVNode("div", _hoisted_5, [
        _createVNode(_unref(LabeledInput), {
          value: privateKeySecretName.value,
          "onUpdate:value": _cache[3] || (_cache[3] = ($event: any) => ((privateKeySecretName).value = $event)),
          label: _unref(t)('certManager.issuer.acme.privateKeySecret'),
          tooltip: secretNamespaceTooltip.value,
          mode: __props.mode,
          rules: __props.rules.privateKeySecret || [],
          required: "",
          "data-testid": "cert-manager-issuer-acme-private-key-secret"
        }, null, 8, ["value", "label", "tooltip", "mode", "rules"])
      ]),
      _cache[8] || (_cache[8] = _createTextVNode()),
      _createElementVNode("div", _hoisted_6, [
        _createVNode(_unref(LabeledInput), {
          value: __props.value.preferredChain,
          "onUpdate:value": _cache[4] || (_cache[4] = ($event: any) => ((__props.value.preferredChain) = $event)),
          label: _unref(t)('certManager.issuer.acme.preferredChain'),
          tooltip: _unref(t)('certManager.issuer.acme.preferredChainTooltip'),
          mode: __props.mode
        }, null, 8, ["value", "label", "tooltip", "mode"])
      ])
    ]),
    _cache[11] || (_cache[11] = _createTextVNode()),
    _createVNode(_unref(Checkbox), {
      value: __props.value.skipTLSVerify,
      "onUpdate:value": _cache[5] || (_cache[5] = ($event: any) => ((__props.value.skipTLSVerify) = $event)),
      label: _unref(t)('certManager.issuer.acme.skipTLSVerify'),
      mode: __props.mode,
      class: "mmb-5"
    }, null, 8, ["value", "label", "mode"]),
    _cache[12] || (_cache[12] = _createTextVNode()),
    _createElementVNode("h3", null, _toDisplayString(_unref(t)('certManager.issuer.tab.solvers')), 1),
    _cache[13] || (_cache[13] = _createTextVNode()),
    (!solvers.value.length)
      ? (_openBlock(), _createBlock(Banner, {
          key: 0,
          color: "warning",
          label: _unref(t)('certManager.solver.noneWarning')
        }, null, 8, ["label"]))
      : _createCommentVNode("", true),
    _cache[14] || (_cache[14] = _createTextVNode()),
    _createVNode(_unref(ArrayListGrouped), {
      value: solvers.value,
      "onUpdate:value": _cache[6] || (_cache[6] = ($event: any) => ((solvers).value = $event)),
      "add-label": _unref(t)('certManager.solver.add'),
      "default-add-value": { selector: {}, http01: { ingress: {} } },
      mode: __props.mode
    }, {
      default: _withCtx(({ row, i }) => [
        _createVNode(AcmeSolver, {
          value: row.value,
          index: i,
          mode: __props.mode
        }, null, 8, ["value", "index", "mode"])
      ]),
      _: 1
    }, 8, ["value", "add-label", "mode"])
  ]))
}
}

})